import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api/axios";
import BrandLogo from "../components/shared/BrandLogo";
import { Package, CheckCircle, Truck, Clock, MapPin } from "lucide-react";

const STATUS_STEPS = ["Received", "In Progress", "Shipped", "Delivered"];

const STATUS_CONFIG = {
  Received: { icon: Clock, color: "text-blue-600", bg: "bg-blue-100", label: "Order Received" },
  "In Progress": { icon: Package, color: "text-amber-600", bg: "bg-amber-100", label: "In Progress" },
  Shipped: { icon: Truck, color: "text-purple-600", bg: "bg-purple-100", label: "Shipped" },
  Delivered: { icon: CheckCircle, color: "text-green-600", bg: "bg-green-100", label: "Delivered" },
};

const OrderTrackingPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/order/${orderId}/track`)
      .then((res) => setOrder(res.data))
      .catch((err) => {
        const msg = err.response?.data?.error || "Order not found. Please check the order ID.";
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [orderId]);

  const currentStepIndex = order ? STATUS_STEPS.indexOf(order.status) : -1;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-8 text-center max-w-sm w-full">
          <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md border-2 border-red-200 p-8 text-center max-w-sm w-full">
          <div className="text-5xl mb-4">📦</div>
          <h2 className="text-xl font-bold text-red-700 mb-2">Order Not Found</h2>
          <p className="text-slate-600 text-sm mb-6">{error}</p>
          <Link to="/" className="inline-flex items-center gap-2 bg-slate-900 text-white font-bold text-sm px-6 py-3 rounded-lg hover:bg-slate-800 transition-colors">
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  const StatusIcon = STATUS_CONFIG[order.status]?.icon || Clock;
  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG["Received"];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-slate-200/50 shadow-sm">
        <div className="max-w-lg mx-auto px-4 py-3">
          <BrandLogo />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-extrabold text-slate-900 mb-1">Order Tracking</h1>
          <p className="text-slate-500 text-sm">Order ID: <span className="font-mono font-semibold text-slate-700">{order.id}</span></p>
        </div>

        {/* Current Status Card */}
        <div className={`bg-white rounded-lg p-5 shadow-md border border-slate-200 mb-6 flex items-center gap-4`}>
          <div className={`w-14 h-14 rounded-lg ${statusConfig.bg} flex items-center justify-center shrink-0`}>
            <StatusIcon size={28} className={statusConfig.color} />
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-0.5">Current Status</p>
            <p className={`text-xl font-extrabold ${statusConfig.color}`}>{statusConfig.label}</p>
            {order.city && order.state && (
              <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                <MapPin size={12} />
                Shipping to {order.city}, {order.state}
              </p>
            )}
          </div>
        </div>

        {/* Progress Tracker */}
        <div className="bg-white rounded-lg p-5 shadow-md border border-slate-200 mb-6">
          <h2 className="text-sm font-bold text-slate-800 mb-4">Order Progress</h2>
          <div className="relative">
            {/* Progress Line */}
            <div className="absolute left-5 top-5 bottom-5 w-0.5 bg-slate-200" />
            <div
              className="absolute left-5 top-5 w-0.5 bg-green-500 transition-all duration-700"
              style={{ height: currentStepIndex >= 0 ? `${(currentStepIndex / (STATUS_STEPS.length - 1)) * 100}%` : "0%" }}
            />

            <div className="space-y-4">
              {STATUS_STEPS.map((step, idx) => {
                const isDone = idx <= currentStepIndex;
                const isCurrent = idx === currentStepIndex;
                const cfg = STATUS_CONFIG[step];
                const StepIcon = cfg.icon;
                return (
                  <div key={step} className="relative flex items-center gap-4 pl-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 shrink-0 transition-colors duration-150
                      ${isDone ? "bg-green-500 text-white shadow-md" : "bg-slate-100 text-slate-400"}`}>
                      <StepIcon size={16} />
                    </div>
                    <div>
                      <p className={`text-sm font-bold ${isDone ? "text-slate-900" : "text-slate-400"}`}>{cfg.label}</p>
                      {isCurrent && (
                        <p className="text-xs text-green-600 font-semibold">Current</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* QR Code Assigned */}
        {order.qrCodeId && (
          <div className="bg-green-50 rounded-lg p-5 shadow-sm border border-green-200 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
                <CheckCircle size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-0.5">QR Code Assigned</p>
                <p className="text-lg font-extrabold text-green-900 font-mono">{order.qrCodeId}</p>
                <Link
                  to={`/${order.qrCodeId}`}
                  className="text-xs font-bold text-green-700 underline hover:text-green-800"
                >
                  Set up your QR sticker →
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Plan Info */}
        <div className="bg-white rounded-lg p-5 shadow-sm border border-slate-200 mb-6">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Plan</p>
          <p className="text-base font-bold text-slate-800 capitalize">{order.planType || "Individual"}</p>
          <p className="text-xs text-slate-400 mt-1">Ordered on {new Date(order.createdAt).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}</p>
        </div>

        {/* Help */}
        <div className="text-center">
          <p className="text-sm text-slate-500">
            Need help?{" "}
            <a href="mailto:contact@dityatechnologies.in" className="text-red-600 font-semibold hover:underline">
              contact@dityatechnologies.in
            </a>
          </p>
          <Link to="/" className="inline-block mt-3 text-sm text-slate-400 hover:text-slate-600 font-medium">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderTrackingPage;
